/** Validation and response shapes shared by the entry forms and their API routes. */

import { DIVISIONS, PASSES } from '@/constants/event'

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export type FieldErrors<Field extends string> = Partial<Record<Field, string>>

export const cleanText = (value: unknown, max: number): string => {
    return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim().slice(0, max) : ''
}

export interface TeamEntryInput {
    division: string
    captainName: string
    captainEmail: string
    partnerName: string
    teamRating: string
}

export type TeamEntryField = keyof TeamEntryInput

export type TeamEntryStatus = 'entered' | 'waitlist' | 'duplicate'

export type TeamEntryResponse =
    | { ok: true, status: TeamEntryStatus }
    | { ok: false, message: string, fieldErrors?: FieldErrors<TeamEntryField> }

export const readTeamEntry = (body: Record<string, unknown>): TeamEntryInput => ({
    division: cleanText(body.division, 40),
    captainName: cleanText(body.captainName, 80),
    captainEmail: cleanText(body.captainEmail, 160).toLowerCase(),
    partnerName: cleanText(body.partnerName, 80),
    teamRating: cleanText(body.teamRating, 12)
})

export const validateTeamEntry = (input: TeamEntryInput): FieldErrors<TeamEntryField> => {
    const errors: FieldErrors<TeamEntryField> = {}

    if (!DIVISIONS.some((division) => division.id === input.division)) errors.division = 'Choose a division.'
    if (input.captainName.length < 2) errors.captainName = 'Enter your full name.'
    if (!EMAIL_PATTERN.test(input.captainEmail)) errors.captainEmail = 'Enter a valid email address.'
    if (input.partnerName.length < 2) errors.partnerName = "Enter your partner's full name."

    if (input.teamRating) {
        const rating = Number(input.teamRating)
        if (!/^\d(\.\d{1,3})?$/.test(input.teamRating) || rating < 1 || rating > 8.5) {
            errors.teamRating = 'Use a rating like 4.25, or leave it empty.'
        }
    }

    return errors
}

export interface TicketListInput {
    name: string
    email: string
    pass: string
    partySize: number
}

export type TicketListField = keyof TicketListInput

export type TicketListStatus = 'added' | 'updated'

export type TicketListResponse =
    | { ok: true, status: TicketListStatus }
    | { ok: false, message: string, fieldErrors?: FieldErrors<TicketListField> }

export const MAX_PARTY_SIZE = 10

export const readTicketList = (body: Record<string, unknown>): TicketListInput => ({
    name: cleanText(body.name, 80),
    email: cleanText(body.email, 160).toLowerCase(),
    pass: cleanText(body.pass, 40),
    partySize: Math.round(Number(body.partySize))
})

export const validateTicketList = (input: TicketListInput): FieldErrors<TicketListField> => {
    const errors: FieldErrors<TicketListField> = {}

    if (!EMAIL_PATTERN.test(input.email)) errors.email = 'Enter a valid email address.'
    if (!PASSES.some((pass) => pass.id === input.pass)) errors.pass = 'Choose a pass.'
    if (!Number.isInteger(input.partySize) || input.partySize < 1 || input.partySize > MAX_PARTY_SIZE) {
        errors.partySize = `Choose between 1 and ${MAX_PARTY_SIZE} people.`
    }

    return errors
}

export type SpotsResponse =
    | { ok: true, counts: Record<string, number> }
    | { ok: false }
